import React, { useState, useTransition } from "react";
import { CheckCircle2 } from "../../icons";
import { Button } from "../../button";
import { Card, CardContent } from "../../card";
import { Input } from "../../input";
import { Textarea } from "../../textarea";
import { FileInput } from "../file-input";
import { FormField } from "../form-shell";
import { apiCall, ApiError } from "../../api/client";
import { apply } from "../../api/contracts/hiring-apply";

interface Props {
  jobSlug: string;
  jobTitle?: string;
}

type SubmitState =
  | { kind: "idle" }
  | { kind: "submitted"; note: "created" | "already_applied" }
  | { kind: "error"; message: string };

const MAX_RESUME_BYTES = 5_000_000;
const RESUME_ACCEPT = "application/pdf,image/png,image/jpeg,text/plain";

export function ApplyForm({ jobSlug }: Props) {
  const [state, setState] = useState<SubmitState>({ kind: "idle" });
  const [pending, startTransition] = useTransition();
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [resumeError, setResumeError] = useState<string | null>(null);

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const formEl = e.currentTarget;
    const form = new FormData(formEl);
    form.set("job_slug", jobSlug);
    if (resumeFile) {
      form.set("resume", resumeFile, resumeFile.name);
    }
    const honeypot = (form.get("website") ?? "").toString();
    if (honeypot.trim()) {
      // Silent accept — the bot believes it succeeded.
      setState({ kind: "submitted", note: "created" });
      return;
    }
    startTransition(async () => {
      try {
        // #44447 — go through apiCall + the hiring-apply contract (FormData
        // body mode). No raw same-origin fetch; envelope errors surface as ApiError.
        const data = await apiCall(apply, { body: form });
        setState({ kind: "submitted", note: data.note });
      } catch (err) {
        setState({
          kind: "error",
          message:
            err instanceof ApiError
              ? err.error
              : err instanceof Error
                ? err.message
                : "Submission failed.",
        });
      }
    });
  }

  if (state.kind === "submitted") {
    return (
      <Card className="mt-4">
        <CardContent className="flex items-start gap-3 px-6 py-2">
          <CheckCircle2
            aria-hidden
            className="mt-0.5 size-5 text-success"
          />
          <div className="flex flex-col gap-1">
            <p className="type-body text-foreground">
              {state.note === "already_applied"
                ? "We already have an application from you for this role."
                : "Application received."}
            </p>
            <p className="type-body text-muted-foreground">
              {state.note === "already_applied"
                ? "Thanks for the interest — the hiring team will be in touch."
                : "We'll review it and get back to you within a week."}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <form className="mt-4 space-y-3" onSubmit={onSubmit}>
      <FormField label="Full name" required htmlFor="apply-name" muted>
        <Input
          id="apply-name"
          name="full_name"
          required
          autoComplete="name"
          placeholder="Casey Rivera"
        />
      </FormField>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FormField label="Email" required htmlFor="apply-email" muted>
          <Input
            id="apply-email"
            name="email"
            type="email"
            required
            autoComplete="email"
            placeholder="casey@example.com"
          />
        </FormField>
        <FormField label="Phone (optional)" htmlFor="apply-phone" muted>
          <Input
            id="apply-phone"
            name="phone"
            type="tel"
            autoComplete="tel"
          />
        </FormField>
      </div>
      <FormField label="LinkedIn URL (optional)" htmlFor="apply-linkedin" muted>
        <Input
          id="apply-linkedin"
          name="linkedin_url"
          type="url"
          placeholder="https://linkedin.com/in/casey"
        />
      </FormField>
      <FormField label="Why this role?" htmlFor="apply-message" muted>
        <Textarea
          id="apply-message"
          name="message"
          className="min-h-32"
          placeholder="Tell us about a project you're proud of."
        />
      </FormField>
      <FormField
        label="Resume (PDF, PNG, JPG, or TXT — max 5MB)"
        error={resumeError ?? undefined}
        muted
      >
        <FileInput
          value={resumeFile}
          onChange={(f) => {
            setResumeFile(f);
            setResumeError(null);
          }}
          accept={RESUME_ACCEPT}
          maxBytes={MAX_RESUME_BYTES}
          onError={setResumeError}
          hint="PDF, PNG, JPG, or TXT. Max 5 MB."
        />
      </FormField>
      {/* Honeypot. Real users never see this. */}
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden
        className="hidden"
      />
      {state.kind === "error" ? (
        <p className="type-body text-destructive">{state.message}</p>
      ) : null}
      <Button type="submit" disabled={pending}>
        {pending ? "Sending…" : "Submit application"}
      </Button>
    </form>
  );
}
